// Asking before something that can't be taken back, in place (AdminPerson's "Revoke ... ? Keep it"),
// and the dialog the boards open over a page (AdminPerson's "Grant Sam a product").
import { useEffect, useId, useRef } from 'react'
import { Button } from './Page.jsx'
import { Icon } from '../shell/Icons.jsx'

// In place, under the row it's about:
//   <ConfirmPanel text="Revoke The Funding Engine? Sam loses access straight away. ..." confirmLabel="Revoke access"
//     cancelLabel="Keep it" onConfirm={revoke} onCancel={close} busy={revoking} error={error} />
export function ConfirmPanel({ text, confirmLabel, cancelLabel = 'Keep it', onConfirm, onCancel, busy = false, error }) {
  return (
    <div className="ad-confirm" role="group" aria-label={confirmLabel}>
      <span className="ad-confirm-text">
        {text}
        {error && <span className="ad-error ad-confirm-error" role="alert">{error.message || error}</span>}
      </span>
      <span className="ad-confirm-actions">
        <Button kind="ink" size="sm" busy={busy} onClick={onConfirm}>{confirmLabel}</Button>
        <Button size="sm" onClick={onCancel} disabled={busy}>{cancelLabel}</Button>
      </span>
    </div>
  )
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

// A dialog over the page, with a grey veil. Escape, the cross or a click on the veil closes it (unless
// busy); focus goes into it and back to what opened it; Tab stays inside it.
//   <Dialog title="Grant Sam a product" onClose={close} footer={<><Button ...>Grant it</Button></>}>...</Dialog>
// eyebrow: the small capitals over the heading, where a board draws them (Referrals' Qualify, Mark paid)
export function Dialog({ title, eyebrow, onClose, children, footer, wide = false, busy = false, closeLabel = 'Close' }) {
  const box = useRef(null)
  const titleId = useId()
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  const busyRef = useRef(busy)
  busyRef.current = busy

  useEffect(() => {
    const before = document.activeElement
    const first = box.current?.querySelector('input, select, textarea') || box.current?.querySelector(FOCUSABLE)
    first?.focus()
    const onKey = e => {
      if (e.key === 'Escape' && !busyRef.current) { e.stopPropagation(); closeRef.current?.() }
      if (e.key === 'Tab' && box.current) {
        const items = [...box.current.querySelectorAll(FOCUSABLE)]
        if (!items.length) return
        const [a, z] = [items[0], items[items.length - 1]]
        if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus() }
        else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus() }
      }
    }
    document.addEventListener('keydown', onKey, true)
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey, true)
      document.body.style.overflow = overflow
      if (before && typeof before.focus === 'function') before.focus()
    }
  }, [])

  return (
    <div className="ad-veil" onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose?.() }}>
      <div ref={box} className={`ad-dialog${wide ? ' ad-dialog--wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="ad-dialog-head">
          {eyebrow
            ? <span className="ad-dialog-heads"><span className="ad-eyebrow">{eyebrow}</span><h2 className="ad-dialog-title" id={titleId}>{title}</h2></span>
            : <h2 className="ad-dialog-title" id={titleId}>{title}</h2>}
          <button type="button" className="ad-close" aria-label={closeLabel} onClick={onClose} disabled={busy}>
            <Icon name="close" size={14} />
          </button>
        </div>
        {children}
        {footer && <div className="ad-dialog-foot">{footer}</div>}
      </div>
    </div>
  )
}
